import {
  CraftsmanCostProps,
  PropertyIdentification,
  getCraftsmanCostDataset,
  getZipMultiplier,
  isDefined,
  isUndefined,
} from '@aloft/toolkit-shared';
import { createSelector } from '@reduxjs/toolkit';

import { selectCostProps } from './costSelectors/selectCostProps.ts';
import { selectAllPropertyIdentification } from './propertyDataSelectors/propertyIdentificationSelectors.ts';

const calculateZipCodeMultiplier = (
  costProps: CraftsmanCostProps,
  propertyIdentification?: PropertyIdentification,
) => {
  if (isUndefined(propertyIdentification) || isUndefined(costProps?.datasetVersion)) {
    return undefined;
  }

  const dataset = getCraftsmanCostDataset(costProps.datasetVersion);
  const {
    address: { PostalCode: zipCode },
  } = propertyIdentification;

  if (isUndefined(zipCode)) {
    return undefined;
  }

  return { zipCode, multiplier: getZipMultiplier(dataset, zipCode) };
};

const selectZipCodeMultipliers = createSelector(
  [selectCostProps, selectAllPropertyIdentification],
  (costProps, propertyIdentification) =>
    Object.fromEntries(
      propertyIdentification.map((property) => [
        property.id,
        calculateZipCodeMultiplier(costProps, property),
      ]),
    ),
);

export const selectZipCodeMultipliersForOrder = createSelector(
  [selectZipCodeMultipliers],
  (zipCodeMultipliers) => {
    const multipliers: { zipCode: string; multiplier: number }[] = [];

    Object.values(zipCodeMultipliers).forEach((zip) => {
      if (
        isDefined(zip) &&
        isDefined(zip?.zipCode) &&
        multipliers.findIndex((multiplier) => multiplier.zipCode === zip.zipCode) === -1
      ) {
        multipliers.push(zip);
      }
    });

    return multipliers.sort((a, b) => a.zipCode.localeCompare(b.zipCode));
  },
);
